/**
 * useProjects Hook
 * V140: 프로젝트 관리 MVP
 * V74: 성능 최적화 - 성능 측정 로그 추가
 *
 * 프로젝트 CRUD 및 조회 기능을 제공합니다.
 * useOrgManagementScope와 연동하여 스코프 기반 조회를 수행합니다.
 *
 * **V74 개선사항:**
 * - BE: N+1 문제 제거 (getAll 배치 조회)
 * - FE: 성능 측정 로그 추가 (networkTimeMs)
 * - 중복 요청 방지 강화
 *
 * **사용 예시:**
 * ```tsx
 * function OrgProjectsPage() {
 *   const { scopeOrgUnitIds } = useOrgManagementScope();
 *   const { projects, isLoading, refetch, createProject, deleteProjects } = useProjects({
 *     scopeOrgUnitIds,
 *   });
 *
 *   return (
 *     <div>
 *       {projects.map((project) => (
 *         <ProjectCard key={project.id} project={project} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  ProjectWithRevision,
  ProjectStatus,
  ListProjectsRequest,
  ListProjectsResponse,
  CreateProjectRequest,
  CreateProjectResponse,
  UpdateProjectMasterRequest,
  UpdateProjectMasterResponse,
  EditProjectRevisionRequest,
  EditProjectRevisionResponse,
  RenewProjectRequest,
  RenewProjectResponse,
  DeleteProjectsRequest,
  DeleteProjectsResponse,
  GetProjectHistoryRequest,
  GetProjectHistoryResponse,
  Project,
  ProjectRevision,
  // V83: 예약 갱신 타입
  ScheduleProjectRenewalRequest,
  ScheduleProjectRenewalResponse,
  CancelProjectRenewalReservationRequest,
  CancelProjectRenewalReservationResponse,
  GetProjectRenewalReservationsRequest,
  GetProjectRenewalReservationsResponse,
  ProjectRenewalReservation,
  ProjectRenewalReservationStatus,
} from '../types/project';

/**
 * useProjects Hook 옵션
 */
export interface UseProjectsOptions {
  /**
   * @deprecated V69 (2026-05-20): BE 가 더 이상 신뢰하지 않음. caller uid 기준으로 BE 가 직접 계산.
   * 호환성 위해 유지하되, BE 는 무시함.
   */
  scopeOrgUnitIds: string[];
  /** 상태 필터 (선택) */
  status?: ProjectStatus;
  /** 검색어 (선택) */
  search?: string;
  /**
   * V69 (2026-05-20): 국가 필터.
   * - 'ALL' 또는 undefined: BE-side scope 내 KR+CN 모두
   * - 'KR' / 'CN': 해당 국가만
   * caller 가 manager/exec/admin/CEO 가 아니면 BE 가 본인 회사 scope 외 데이터 자동 차단.
   */
  companyCode?: 'KR' | 'CN' | 'ALL';
  /** 자동 조회 비활성화 */
  enabled?: boolean;
}

/**
 * useProjects Hook 반환 타입
 */
export interface UseProjectsResult {
  // 데이터
  /** 프로젝트 목록 (현재 리비전 포함) */
  projects: ProjectWithRevision[];
  /** 선택된 프로젝트 */
  selectedProject: ProjectWithRevision | null;
  /** 선택된 프로젝트의 리비전 이력 */
  revisionHistory: ProjectRevision[];

  // 상태
  /** 목록 로딩 중 */
  isLoading: boolean;
  /** 상세 로딩 중 */
  isLoadingDetail: boolean;
  /** 뮤테이션 로딩 중 */
  isMutating: boolean;
  /** 에러 */
  error: Error | null;

  // 조회
  /** 목록 다시 조회 */
  refetch: () => Promise<void>;
  /** 프로젝트 상세 및 리비전 이력 조회 */
  fetchProjectHistory: (projectId: string) => Promise<void>;

  // 뮤테이션
  /** 프로젝트 생성 */
  createProject: (data: CreateProjectRequest) => Promise<CreateProjectResponse>;
  /** 프로젝트 마스터 정보 수정 */
  updateProjectMaster: (data: UpdateProjectMasterRequest) => Promise<UpdateProjectMasterResponse>;
  /** 현재 리비전 수정 (덮어쓰기) */
  editProjectRevision: (data: EditProjectRevisionRequest) => Promise<EditProjectRevisionResponse>;
  /** 새 리비전 생성 (갱신) */
  renewProject: (data: RenewProjectRequest) => Promise<RenewProjectResponse>;
  /** 프로젝트 삭제 */
  deleteProjects: (projectIds: string[]) => Promise<DeleteProjectsResponse>;

  // V83: 예약 갱신
  /** 예약 갱신 생성 */
  scheduleProjectRenewal: (data: ScheduleProjectRenewalRequest) => Promise<ScheduleProjectRenewalResponse>;
  /** 예약 갱신 취소 */
  cancelProjectRenewalReservation: (reservationId: string) => Promise<CancelProjectRenewalReservationResponse>;
  /** 프로젝트별 예약 목록 조회 */
  getProjectRenewalReservations: (projectId: string, statusFilter?: ProjectRenewalReservationStatus) => Promise<ProjectRenewalReservation[]>;

  // 헬퍼
  /** 선택된 프로젝트 설정 */
  setSelectedProject: (project: ProjectWithRevision | null) => void;
  /** 디버그 정보 (V74: networkTimeMs 추가) */
  debugMeta: { scopeCount: number; totalProjects: number; networkTimeMs?: number } | null;
}

/**
 * useProjects Hook
 */
export function useProjects(options: UseProjectsOptions): UseProjectsResult {
  const { scopeOrgUnitIds, status, search, companyCode, enabled = true } = options;

  // 상태
  const [projects, setProjects] = useState<ProjectWithRevision[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectWithRevision | null>(null);
  const [revisionHistory, setRevisionHistory] = useState<ProjectRevision[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [debugMeta, setDebugMeta] = useState<{ scopeCount: number; totalProjects: number; networkTimeMs?: number } | null>(null);

  // 중복 요청 방지
  const isFetching = useRef(false);

  // Callable 함수들
  const listProjectsFn = httpsCallable<ListProjectsRequest, ListProjectsResponse>(
    functions!,
    'listProjects'
  );
  const createProjectFn = httpsCallable<CreateProjectRequest, CreateProjectResponse>(
    functions!,
    'createProject'
  );
  const updateProjectMasterFn = httpsCallable<UpdateProjectMasterRequest, UpdateProjectMasterResponse>(
    functions!,
    'updateProjectMaster'
  );
  const editProjectRevisionFn = httpsCallable<EditProjectRevisionRequest, EditProjectRevisionResponse>(
    functions!,
    'editProjectRevision'
  );
  const renewProjectFn = httpsCallable<RenewProjectRequest, RenewProjectResponse>(
    functions!,
    'renewProject'
  );
  const deleteProjectsFn = httpsCallable<DeleteProjectsRequest, DeleteProjectsResponse>(
    functions!,
    'deleteProjects'
  );
  const getProjectHistoryFn = httpsCallable<GetProjectHistoryRequest, GetProjectHistoryResponse>(
    functions!,
    'getProjectHistory'
  );
  // V83: 예약 갱신 callable
  const scheduleProjectRenewalFn = httpsCallable<ScheduleProjectRenewalRequest, ScheduleProjectRenewalResponse>(
    functions!,
    'scheduleProjectRenewal'
  );
  const cancelProjectRenewalReservationFn = httpsCallable<CancelProjectRenewalReservationRequest, CancelProjectRenewalReservationResponse>(
    functions!,
    'cancelProjectRenewalReservation'
  );
  const getProjectRenewalReservationsFn = httpsCallable<GetProjectRenewalReservationsRequest, GetProjectRenewalReservationsResponse>(
    functions!,
    'getProjectRenewalReservations'
  );

  /**
   * 프로젝트 목록 조회
   * V74: 성능 측정 로그 추가
   */
  const refetch = useCallback(async () => {
    if (!enabled || scopeOrgUnitIds.length === 0 || isFetching.current) {
      return;
    }

    try {
      isFetching.current = true;
      setIsLoading(true);
      setError(null);

      // V74: 성능 측정 시작
      const startTime = performance.now();

      const result = await listProjectsFn({
        scopeOrgUnitIds,  // V69 (2026-05-20): BE 는 무시. caller uid 기준 BE-side 계산.
        status,
        search,
        // V69 (2026-05-20): 국가 필터. BE 가 caller scope 와 조합하여 안전하게 처리.
        ...(companyCode && companyCode !== 'ALL' ? { companyCode } : {}),
      });

      // V74: 성능 측정 종료
      const endTime = performance.now();
      const networkTimeMs = Math.round(endTime - startTime);

      if (result.data.success) {
        setProjects(result.data.projects);

        // V74: debugMeta에 networkTimeMs 추가
        const meta = {
          ...(result.data.debugMeta || { scopeCount: 0, totalProjects: 0 }),
          networkTimeMs,
        };
        setDebugMeta(meta);

      } else {
        throw new Error('프로젝트 목록 조회 실패');
      }
    } catch (err: any) {
      console.error('[useProjects] 조회 실패:', err);
      setError(err);
      setProjects([]);
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, [enabled, scopeOrgUnitIds, status, search, companyCode]);

  /**
   * 프로젝트 상세 및 리비전 이력 조회
   */
  const fetchProjectHistory = useCallback(async (projectId: string) => {
    try {
      setIsLoadingDetail(true);
      setError(null);

      const result = await getProjectHistoryFn({ projectId });

      if (result.data.success && result.data.project) {
        // 현재 리비전을 포함한 프로젝트 설정
        const projectWithRevision: ProjectWithRevision = {
          ...result.data.project,
          currentRevision: result.data.revisions?.find(
            (r) => r.id === result.data.project!.currentRevisionId
          ),
        };
        setSelectedProject(projectWithRevision);
        setRevisionHistory(result.data.revisions || []);
      } else {
        throw new Error(result.data.error || '프로젝트 상세 조회 실패');
      }
    } catch (err: any) {
      console.error('[useProjects] 상세 조회 실패:', err);
      setError(err);
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  /**
   * 프로젝트 생성
   */
  const createProject = useCallback(async (data: CreateProjectRequest): Promise<CreateProjectResponse> => {
    try {
      setIsMutating(true);
      setError(null);

      const result = await createProjectFn(data);

      if (result.data.success) {
        // 목록 새로고침
        await refetch();
      }

      return result.data;
    } catch (err: any) {
      console.error('[useProjects] 생성 실패:', err);
      setError(err);
      return { success: false, error: err.message };
    } finally {
      setIsMutating(false);
    }
  }, [refetch]);

  /**
   * 프로젝트 마스터 정보 수정
   */
  const updateProjectMaster = useCallback(async (data: UpdateProjectMasterRequest): Promise<UpdateProjectMasterResponse> => {
    try {
      setIsMutating(true);
      setError(null);

      const result = await updateProjectMasterFn(data);

      if (result.data.success) {
        // 목록 새로고침
        await refetch();
        // 상세도 새로고침
        if (selectedProject?.id === data.projectId) {
          await fetchProjectHistory(data.projectId);
        }
      }

      return result.data;
    } catch (err: any) {
      console.error('[useProjects] 마스터 수정 실패:', err);
      setError(err);
      return { success: false, error: err.message };
    } finally {
      setIsMutating(false);
    }
  }, [refetch, fetchProjectHistory, selectedProject]);

  /**
   * 현재 리비전 수정 (덮어쓰기)
   */
  const editProjectRevision = useCallback(async (data: EditProjectRevisionRequest): Promise<EditProjectRevisionResponse> => {
    try {
      setIsMutating(true);
      setError(null);

      const result = await editProjectRevisionFn(data);

      if (result.data.success) {
        // 목록 새로고침
        await refetch();
        // 상세도 새로고침
        if (selectedProject?.id === data.projectId) {
          await fetchProjectHistory(data.projectId);
        }
      }

      return result.data;
    } catch (err: any) {
      console.error('[useProjects] 리비전 수정 실패:', err);
      setError(err);
      return { success: false, error: err.message };
    } finally {
      setIsMutating(false);
    }
  }, [refetch, fetchProjectHistory, selectedProject]);

  /**
   * 새 리비전 생성 (갱신)
   */
  const renewProject = useCallback(async (data: RenewProjectRequest): Promise<RenewProjectResponse> => {
    try {
      setIsMutating(true);
      setError(null);

      const result = await renewProjectFn(data);

      if (result.data.success) {
        // 목록 새로고침
        await refetch();
        // 상세도 새로고침
        if (selectedProject?.id === data.projectId) {
          await fetchProjectHistory(data.projectId);
        }
      }

      return result.data;
    } catch (err: any) {
      console.error('[useProjects] 갱신 실패:', err);
      setError(err);
      return { success: false, error: err.message };
    } finally {
      setIsMutating(false);
    }
  }, [refetch, fetchProjectHistory, selectedProject]);

  /**
   * 프로젝트 삭제
   */
  const deleteProjects = useCallback(async (projectIds: string[]): Promise<DeleteProjectsResponse> => {
    try {
      setIsMutating(true);
      setError(null);

      const result = await deleteProjectsFn({ projectIds });

      if (result.data.success) {
        // 목록 새로고침
        await refetch();
        // 선택된 프로젝트가 삭제되었으면 초기화
        if (selectedProject && projectIds.includes(selectedProject.id)) {
          setSelectedProject(null);
          setRevisionHistory([]);
        }
      }

      return result.data;
    } catch (err: any) {
      console.error('[useProjects] 삭제 실패:', err);
      setError(err);
      return { success: false, error: err.message };
    } finally {
      setIsMutating(false);
    }
  }, [refetch, selectedProject]);

  /**
   * V83: 예약 갱신 생성
   */
  const scheduleProjectRenewal = useCallback(async (data: ScheduleProjectRenewalRequest): Promise<ScheduleProjectRenewalResponse> => {
    try {
      setIsMutating(true);
      setError(null);

      const result = await scheduleProjectRenewalFn(data);

      if (result.data.success) {
        // reservation created
      }

      return result.data;
    } catch (err: any) {
      console.error('[useProjects] 예약 생성 실패:', err);
      setError(err);
      return { success: false, error: err.message };
    } finally {
      setIsMutating(false);
    }
  }, []);

  /**
   * V83: 예약 갱신 취소
   */
  const cancelProjectRenewalReservation = useCallback(async (reservationId: string): Promise<CancelProjectRenewalReservationResponse> => {
    try {
      setIsMutating(true);
      setError(null);

      const result = await cancelProjectRenewalReservationFn({ reservationId });

      if (result.data.success) {
        // reservation cancelled
      }

      return result.data;
    } catch (err: any) {
      console.error('[useProjects] 예약 취소 실패:', err);
      setError(err);
      return { success: false, error: err.message };
    } finally {
      setIsMutating(false);
    }
  }, []);

  /**
   * V83: 프로젝트별 예약 목록 조회
   */
  const getProjectRenewalReservations = useCallback(async (projectId: string, statusFilter?: ProjectRenewalReservationStatus): Promise<ProjectRenewalReservation[]> => {
    try {
      const result = await getProjectRenewalReservationsFn({ projectId, status: statusFilter });

      if (result.data.success) {
        return result.data.reservations || [];
      }

      return [];
    } catch (err: any) {
      console.error('[useProjects] 예약 조회 실패:', err);
      return [];
    }
  }, []);

  // scopeOrgUnitIds 변경 시 자동 조회
  useEffect(() => {
    if (enabled && scopeOrgUnitIds.length > 0) {
      refetch();
    }
  }, [enabled, scopeOrgUnitIds, status, search, companyCode]);

  return {
    // 데이터
    projects,
    selectedProject,
    revisionHistory,

    // 상태
    isLoading,
    isLoadingDetail,
    isMutating,
    error,

    // 조회
    refetch,
    fetchProjectHistory,

    // 뮤테이션
    createProject,
    updateProjectMaster,
    editProjectRevision,
    renewProject,
    deleteProjects,

    // V83: 예약 갱신
    scheduleProjectRenewal,
    cancelProjectRenewalReservation,
    getProjectRenewalReservations,

    // 헬퍼
    setSelectedProject,
    debugMeta,
  };
}
